// src/website/components/booking/barberSelection.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  SimpleGrid,
  Radio,
  Flex,
  Image,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { collection, getDocs, query, where } from "firebase/firestore"; // Import 'query' and 'where'
import { getStorage } from "firebase/storage"; // Import Firebase Storage functions
import { db } from "firebaseConfig";
import { getCachedDownloadURL } from "utils/storageUrlCache";
import { getCachedList, setCachedList } from "utils/listCache";

import { useTranslation } from 'react-i18next'; // Import useTranslation

const DEFAULT_PROFILE_IMAGE = "/assets/img/profile/default_profile.png";
const BARBERS_CACHE_KEY = "barbers";

const BarberSelection = ({ selectedBarber, setSelectedBarber }) => {
  const { t } = useTranslation(); // Initialize the translation hook
  // Seed from the last-known barber list (with resolved image URLs) so a
  // returning visitor sees real photos immediately instead of placeholders,
  // while the effects below fetch fresh data in the background.
  const [barbers, setBarbers] = useState(() => getCachedList(BARBERS_CACHE_KEY) || []);
  const [barbersWithImages, setBarbersWithImages] = useState(
    () => getCachedList(BARBERS_CACHE_KEY) || []
  );
  const [loadingBarbers, setLoadingBarbers] = useState(() => getCachedList(BARBERS_CACHE_KEY) === null);
  const [errorBarbers, setErrorBarbers] = useState(null);
  
  const toast = useToast();

  useEffect(() => {
    const fetchBarbers = async () => {
      try {
        const barbersCollection = collection(db, "users");
        // Create a query against the collection to filter barbers with status "Enabled"
        const q = query(barbersCollection, where("status", "==", "Enabled"));
        const barbersSnapshot = await getDocs(q);
        const barbersList = barbersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBarbers(barbersList);
        // Keep whatever image URL each barber already has (from the cached
        // list seeded above) so already-known photos don't flash back to
        // the placeholder; new barbers fall back to the placeholder until
        // their own Storage lookup resolves below.
        setBarbersWithImages((prev) =>
          barbersList.map((barber) => {
            const cached = prev.find((item) => item.id === barber.id);
            return { ...barber, imageUrl: cached?.imageUrl || DEFAULT_PROFILE_IMAGE };
          })
        );
        setLoadingBarbers(false);
      } catch (error) {
        console.error("Error fetching barbers: ", error);
        setErrorBarbers("fetch_barbers_error");
        setLoadingBarbers(false);
        toast({
          title: t("error"),
          description: t("fetch_barbers_error"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    fetchBarbers();
  }, [toast, t]);

  // Resolve each barber's Storage download URL independently (cached across
  // visits) and swap it in as soon as it's ready, persisting the refreshed
  // list+URLs so the next visit can paint from cache instantly.
  useEffect(() => {
    if (barbers.length === 0) return;

    const storage = getStorage(); // Initialize Firebase Storage

    barbers.forEach((barber) => {
      if (!barber.profile) return;

      getCachedDownloadURL(storage, `profiles/${barber.profile}`)
        .then((url) => {
          setBarbersWithImages((prev) => {
            const updated = prev.map((item) =>
              item.id === barber.id ? { ...item, imageUrl: url } : item
            );
            setCachedList(BARBERS_CACHE_KEY, updated);
            return updated;
          });
        })
        .catch((error) => {
          console.error(`Error fetching image for barber ${barber.id}:`, error);
          // Already showing the default fallback image, nothing further to do.
        });
    });
  }, [barbers]);

  if (loadingBarbers) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" />
        <Text mt={4}>{t("loading_barbers")}</Text>
      </Box>
    );
  }

  if (errorBarbers) {
    return (
      <Box textAlign="center" py={10}>
        <Text color="red.500">{t(errorBarbers)}</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Text fontSize="xl" mb={4}>
        {t("select_barber")}
      </Text>
      <SimpleGrid columns={[1, 2]} spacing={4}>
        {barbersWithImages.map((barber) => (
          <Radio
            key={barber.id}
            value={barber.id}
            onChange={() => setSelectedBarber(barber)}
            isChecked={selectedBarber?.id === barber.id}
            p={0}
            borderRadius="md"
          >
            <Flex
              borderWidth="1px"
              borderRadius="md"
              p={1}
              align="center"
              cursor="pointer"
              _hover={{ bg: "green.100" }}
              bg={
                selectedBarber?.id === barber.id
                  ? "green.200"
                  : "white"
              } // Light green highlight
              w="100%"
            >
              <Image
                src={barber.imageUrl} 
                boxSize="65px" // Adjust size as needed
                objectFit="cover"
                borderRadius="full"
                mr={4}
                fallbackSrc="/assets/img/profile/default_profile.png" // Fallback image
              />
              <Box>
                <Text fontWeight="bold">{barber.name}</Text>
                <Text fontSize="sm" color="gray.500">
                  {barber.phone}
                </Text>
              </Box>
            </Flex>
          </Radio>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default BarberSelection;
